import { GetInferredFromRaw } from '@thinknimble/tn-models'
import { z } from 'zod'
import { baseModelShape } from '../base-model'
import { organizationShape } from '../user'

export const ReportStatus = {
  NOT_STARTED: 'not_started', // Initial state, never generated
  PENDING: 'pending',
  QUEUED: 'queued', // Task scheduled but not yet picked up by worker
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const

export type ReportStatusType = (typeof ReportStatus)[keyof typeof ReportStatus]

export const reportShape = {
  ...baseModelShape,

  name: z.string(),
  notes: z.string(),
  isSubmitted: z.boolean().default(false),
  isApproved: z.boolean().default(false),
  organization: z.string(),
  organizationRef: z.object(organizationShape),
  defaultTemplate: z.string().uuid().optional().nullable(),
  defaultTemplateRef: z.object({ id: z.string().uuid(), name: z.string() }).optional().nullable(),
  asset: z.string().optional().readonly(),
  dateReportGenerated: z.string().or(z.date()).optional().nullable(),
  status: z.string().optional().nullable(),
  // Progress tracking fields
  startedAt: z.string().or(z.date()).optional().nullable(),
  progressCurrent: z.number().default(0),
  progressTotal: z.number().default(0),
  errorMessage: z.string().optional().nullable(),
  // Present only on the generate-report response: the tracked AsyncJob's id, so
  // the client can optimistically show an in-progress spinner immediately.
  asyncJobId: z.string().uuid().nullable().optional(),
}

export const reportCreateShape = {
  name: z.string(),
  notes: z.string().optional().nullable(),
  organization: z.string(),
  defaultTemplate: z.string().uuid().optional().nullable(),
}

export const reportFilterShape = {
  search: z.string(),
  organization: z.string().array(),
}

export type ExpenseReport = GetInferredFromRaw<typeof reportShape>
export type ExpenseReportFilterShape = GetInferredFromRaw<typeof reportFilterShape>

/**
 * Check if a report should be polled for status updates.
 * Returns true if the report is queued/pending/in_progress and was started within the last 30 minutes.
 */
export function shouldPollReport(report: ExpenseReport): boolean {
  const status = report.status as ReportStatusType | null | undefined

  // Only poll queued, pending, or in_progress reports
  const pollableStatuses: ReportStatusType[] = [
    ReportStatus.PENDING,
    ReportStatus.QUEUED,
    ReportStatus.IN_PROGRESS,
  ]
  if (!status || !pollableStatuses.includes(status)) {
    return false
  }

  // If no startedAt or created, can't determine age - don't poll
  if (!report.startedAt && !report.created) {
    return false
  }

  // If no startedAt, check created date instead
  const startTime = report.startedAt ? new Date(report.startedAt) : new Date(report.created!)
  const now = new Date()
  const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000)

  // Only poll if started within the last 30 minutes
  return startTime > thirtyMinutesAgo
}

/**
 * Check if a report can be cancelled.
 * Only queued reports can be cancelled (before worker picks them up).
 */
export function canCancelReport(report: ExpenseReport): boolean {
  return report.status === ReportStatus.QUEUED
}

/**
 * Get progress percentage for a report.
 * Returns 0-100 or null if progress info isn't available.
 */
export function getReportProgress(report: ExpenseReport): number | null {
  if (!report.progressTotal || report.progressTotal === 0) {
    return null
  }
  return Math.round((report.progressCurrent / report.progressTotal) * 100)
}
